import {
  BaseGraphSecretV3,
  CellId,
  DataConnectionId,
  GraphNodeV3,
  GraphNodeV3Id,
  HexVersionId,
  getCellIdsToSkipForAppRuns,
  getGraphNodeV3Id,
  getGraphNodeV3Outputs,
  getProjectGraphV3,
  notNullish,
  sortGraphNodeV3s,
  stableEmptySet,
  typedObjectEntries,
  typedObjectKeys,
} from "@hex/common";
import {
  EntityId,
  EntityState,
  PayloadAction,
  createEntityAdapter,
  createReducer,
  createSlice,
} from "@reduxjs/toolkit";
import { castDraft } from "immer";
import { isEqual, memoize } from "lodash";

import { getCellReferenceMapV3 } from "../../graph/getCellReferencesV3";
import { GridRowMpFragment } from "../../hex-version-multiplayer/HexVersionMPModel.generated.js";
import { RootState } from "../store";
import {
  IdEntityState,
  createSelector,
  getSelectorsForNullableEntityState,
} from "../utils/index.js";

import { CellContentsMP, CellMP } from "./hexVersionMPSlice";

type GraphNodePrunedState = {
  id: CellId;
  pruned: boolean;
};

export type ProjectGraphV3SliceValue = {
  graphNodes: EntityState<GraphNodeV3<CellMP>, GraphNodeV3Id>;
  projectDataConnections: EntityState<DataConnectionId, DataConnectionId>;
  prunedCellsState: IdEntityState<GraphNodePrunedState>;
};

type UpdateProjectDataConnectionsPayload = {
  hexVersionId: HexVersionId;
  projectDataConnections: DataConnectionId[];
};

type UpdateProjectGraphV3Payload = {
  hexVersionId: HexVersionId;
  cells: Record<CellId, CellMP | undefined>;
  cellContents: Record<CellId, CellContentsMP | undefined>;
  secrets: BaseGraphSecretV3[];
  dataConnections: DataConnectionId[];
  gridRows: GridRowMpFragment[];
};

const graphNodeV3Adapter = createEntityAdapter<
  GraphNodeV3<CellMP>,
  GraphNodeV3Id
>({
  selectId: getGraphNodeV3Id,
  sortComparer: sortGraphNodeV3s,
});

const projectDataConnectionsAdapter = createEntityAdapter<
  DataConnectionId,
  DataConnectionId
>({
  selectId: (id) => id,
  sortComparer: (a, b) => a.localeCompare(b),
});

const graphNodePrunedStateAdapter = createEntityAdapter<GraphNodePrunedState>({
  sortComparer: (a, b) => a.id.localeCompare(b.id),
});

export const initialProjectGraphV3ValueState: ProjectGraphV3SliceValue = {
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
  graphNodes: graphNodeV3Adapter.getInitialState(),
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
  projectDataConnections: projectDataConnectionsAdapter.getInitialState(),
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
  prunedCellsState: graphNodePrunedStateAdapter.getInitialState(),
};

const projectGraphV3ValueSlice = createSlice({
  name: "projectGraphV3",
  initialState: initialProjectGraphV3ValueState,
  reducers: {
    updateProjectGraphV3: (
      state,
      action: PayloadAction<UpdateProjectGraphV3Payload>,
    ) => {
      const { cellContents, cells, dataConnections, gridRows, secrets } =
        action.payload;

      const cellMap = getCellReferenceMapV3(cells, cellContents);
      const { graph } = getProjectGraphV3({
        cells: cellMap,
        dataConnections,
        secrets,
      });

      if (cells != null) {
        const cellsValues = Object.values(cells).filter(notNullish);
        const gridElements = (gridRows ?? [])
          .flatMap((row) => row.gridColumns)
          .flatMap((column) => column.gridElements);

        const cellIdsInApp = new Set(
          gridElements
            .filter(
              (element) =>
                element.type === "CELL" && element.deletedDate == null,
            )
            .map((element) => element.entityId as CellId),
        );

        const cellsInApp = cellsValues.filter((c) => cellIdsInApp.has(c.id));

        const prunedCellIds = getCellIdsToSkipForAppRuns({
          cellReferences: cellMap,
          cellsInApp,
          graphV3: graph,
        });

        const prunedCellState = cellsValues.map((cell) => {
          return { id: cell.id, pruned: prunedCellIds.has(cell.id) };
        });

        graphNodePrunedStateAdapter.setAll(
          state.prunedCellsState,
          prunedCellState,
        );
      }

      const nodeIds = new Set<EntityId>(Object.keys(graph));

      // Remove any nodes that are no longer in the graph
      for (const nodeId of state.graphNodes.ids) {
        if (!nodeIds.has(nodeId)) {
          graphNodeV3Adapter.removeOne(state.graphNodes, nodeId);
        }
      }

      // Update nodes if they have changed, manually checking each field because upsertOne only does shallow compare
      for (const [nodeId, node] of typedObjectEntries(graph)) {
        const oldNode = state.graphNodes.entities[nodeId];
        if (!oldNode || oldNode.type !== node.type) {
          graphNodeV3Adapter.upsertOne(state.graphNodes, node);
        } else {
          const id = getGraphNodeV3Id(node);
          const changes: Partial<GraphNodeV3<CellMP>> = {};
          for (const prop of typedObjectKeys(node)) {
            if (!isEqual(oldNode[prop], node[prop])) {
              changes[prop] = node[prop];
            }
          }
          if (Object.keys(changes).length > 0) {
            graphNodeV3Adapter.updateOne(state.graphNodes, {
              id,
              changes,
            });
          }
        }
      }
    },
    updateProjectDataConnections: (
      state,
      action: PayloadAction<UpdateProjectDataConnectionsPayload>,
    ) => {
      projectDataConnectionsAdapter.setAll(
        state.projectDataConnections,
        action.payload.projectDataConnections,
      );
    },
  },
});

export const projectGraphV3Selectors = {
  getGraphNodeV3Selectors: memoize((hexVersionId: HexVersionId) => {
    const baseSelectors = getSelectorsForNullableEntityState(
      graphNodeV3Adapter,
      (state: RootState) => state.projectGraphV3[hexVersionId]?.graphNodes,
    );

    return {
      ...baseSelectors,
      selectAllOutputNames: createSelector(
        [baseSelectors.selectAll],
        (nodes): ReadonlySet<string> => {
          if (nodes == null) return stableEmptySet();

          return new Set(
            nodes.flatMap((node) =>
              Array.from(getGraphNodeV3Outputs(node).keys()),
            ),
          );
        },
      ),
    };
  }),
  getPrunedCellsSelectors: memoize((hexVersionId: HexVersionId) => {
    const baseSelectors = getSelectorsForNullableEntityState(
      graphNodePrunedStateAdapter,
      (state: RootState) =>
        state.projectGraphV3[hexVersionId]?.prunedCellsState,
    );

    return { ...baseSelectors };
  }),
};

export const { updateProjectGraphV3 } = projectGraphV3ValueSlice.actions;

export const projectGraphV3Actions = {
  ...projectGraphV3ValueSlice.actions,
} as const;

const allActionTypes = new Set(
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
  Object.values(projectGraphV3Actions).map((a) => a.type),
);

type ProjectGraphV3Actions = typeof projectGraphV3ValueSlice.actions;
type ProjectGraphV3ActionPayload = Parameters<
  ProjectGraphV3Actions[keyof ProjectGraphV3Actions]
>[0];

type ProjectGraphV3SliceState = {
  [hexVersionId: string]: ProjectGraphV3SliceValue | undefined;
};

export const projectGraphV3Reducer = createReducer<ProjectGraphV3SliceState>(
  {},
  (builder) =>
    builder.addMatcher(
      (
        action,
      ): action is PayloadAction<
        ProjectGraphV3ActionPayload | UpdateProjectDataConnectionsPayload
      > => allActionTypes.has(action.type),
      (state, action) => {
        state[action.payload.hexVersionId] = castDraft(
          projectGraphV3ValueSlice.reducer(
            state[action.payload.hexVersionId],
            action,
          ),
        );
      },
    ),
);
